import { BASE_URL } from 'constants/env';
import useSWR from 'swr';
import type { DestinationContentModifySchema } from 'typings/destinations/DestinationContentModifySchema';
import type { DestinationContentSchema } from 'typings/destinations/DestinationContentSchema';
import { DestinationContentTranslationModifySchema } from 'typings/destinations/DestinationContentTranslationModifySchema';
import { DestinationContentTranslationSchema } from 'typings/destinations/DestinationContentTranslationSchema';
import type { DestinationModifySchema } from 'typings/destinations/DestinationModifySchema';
import type { DestinationPublicStepSchema } from 'typings/destinations/DestinationPublicStepSchema';
import type { DestinationSchema } from 'typings/destinations/DestinationSchema';
import { get, patch, post, postDelete, put } from 'utils/http';
import type { DestinationTravelThemeSchema } from 'typings/destinations/DestinationTravelThemeSchema';
import type { DestinationContentTypes } from 'typings/destinations/DestinationContentTypeEnum';
import type { PaginatedDestinationSchema } from 'typings/destinations/PaginatedDestinationSchema';
import { DestinationGeoType } from 'constants/destination-geo-type';
import { DestinationStatuses } from 'constants/destination-statuses';

const defaultOptions = {
    headers: {
        'Polarsteps-Api-Version': '58',
    },
};

type GetAllDestinationsParams = {
    query?: string;
    sortColumn?: string;
    sortOrder?: string;
    page: number;
    resultsPerPage?: number;
    onlyWithoutPhotos?: boolean;
    hasGuide?: boolean;
    source?: number;
    status?: number;
    geo_type?: number;
    has_content?: boolean;
    content_type?: DestinationContentTypes | null;
    allHighlightsHaveImages?: boolean;
    onlyMissingTranslations?: boolean;
};

// Destination API

export async function getDestination(destinationId: string) {
    const response = await get<DestinationSchema>(`${BASE_URL}/cms/destinations/${destinationId}`, defaultOptions);
    return response;
}

export async function getDestinationPublicSteps(destinationId: number) {
    const response = await get<DestinationPublicStepSchema[]>(
        `${BASE_URL}/cms/destinations/${destinationId}/public-steps?limit=20`,
        defaultOptions,
    );
    return response;
}

export async function getAllDestinations({
    query,
    sortColumn,
    sortOrder,
    onlyWithoutPhotos,
    hasGuide,
    source,
    page,
    resultsPerPage = 20,
    status,
    geo_type,
    has_content,
    content_type,
    allHighlightsHaveImages,
    onlyMissingTranslations,
}: GetAllDestinationsParams) {
    const params = new URLSearchParams({
        page: `${page}`,
        results_per_page: `${resultsPerPage}`,
    });

    if (onlyWithoutPhotos) {
        params.set('has_photos', 'false');
    }

    if (hasGuide) {
        params.set('has_guide', 'true');
    }

    if (typeof source === 'number') {
        params.set('source', source.toString());
    }

    if (typeof status === 'number') {
        params.set('status', status.toString());
    }

    if (typeof geo_type === 'number') {
        params.set('geo_type', geo_type.toString());
    }

    if (typeof has_content === 'boolean') {
        params.set('has_content', has_content ? 'true' : 'false');
    }

    if (typeof content_type === 'number') {
        params.set('content_type', content_type.toString());
    }

    if (typeof allHighlightsHaveImages === 'boolean') {
        params.set('has_all_highlight_images', allHighlightsHaveImages ? 'true' : 'false');
    }

    if (onlyMissingTranslations) {
        params.set('has_all_translations', 'false');
    }

    if (query?.length) {
        params.set('q', query);
    }

    if (sortColumn) {
        params.set('sort_column', sortColumn);
    }
    if (sortOrder) {
        params.set('sort_order', sortOrder);
    }

    const allDestinations = await get<PaginatedDestinationSchema>(
        `${BASE_URL}/cms/destinations?${params.toString()}`,
        defaultOptions
    );

    return {
        pagination: {
            page: allDestinations.page,
            totalPages: allDestinations.total_pages,
            resultsPerPage,
            totalResults: allDestinations.num_results,
        },
        results: allDestinations.objects,
    };
}

export async function createDestination(destination: DestinationModifySchema) {
    return post<DestinationSchema>(`${BASE_URL}/cms/destinations`, destination, defaultOptions);
}

export async function updateDestination(destinationId: number, requestBody: DestinationModifySchema) {
    return patch<DestinationSchema>(`${BASE_URL}/cms/destinations/${destinationId}`, requestBody, defaultOptions);
}

export async function deleteDestination(destinationId: number) {
    return postDelete(`${BASE_URL}/cms/destinations/${destinationId}`, defaultOptions);
}

export async function generateDestinationContent(destinationId: number) {
    return post<DestinationSchema>(
        `${BASE_URL}/cms/destinations/${destinationId}/generate-content`,
        {},
        {
            headers: {
                'Polarsteps-Api-Version': '71',
            },
        }
    );
}

export async function translateDestinationContent(destinationId: number) {
    return post<DestinationSchema>(
        `${BASE_URL}/cms/destinations/${destinationId}/translate-content`,
        {},
        {
            headers: {
                'Polarsteps-Api-Version': '71',
            },
        }
    );
}

export async function archiveDestination(destination: DestinationSchema) {
    return updateDestination(destination.id, {
        ...destination,
        status: DestinationStatuses.ARCHIVED,
    });
}

export async function unarchiveDestination(destination: DestinationSchema) {
    return updateDestination(destination.id, {
        ...destination,
        status: DestinationStatuses.DRAFT,
    });
}

// Destination content API

export async function updateDestinationContent(contentId: number, requestBody: DestinationContentModifySchema) {
    return patch<DestinationContentSchema>(
        `${BASE_URL}/cms/destinations/contents/${contentId}`,
        requestBody,
        defaultOptions
    );
}

export async function createDestinationContent(destinationId: number, requestBody: DestinationContentModifySchema) {
    return post<DestinationContentSchema>(
        `${BASE_URL}/cms/destinations/${destinationId}/contents`,
        requestBody,
        defaultOptions
    );
}

export async function deleteDestinationContent(contentId: number) {
    return postDelete(`${BASE_URL}/cms/destinations/contents/${contentId}`, defaultOptions);
}

export async function bulkCreateUpdateDestinationContents(
    destinationId: number,
    contents: DestinationContentModifySchema[]
) {
    return patch<DestinationContentSchema[]>(
        `${BASE_URL}/cms/destinations/${destinationId}/contents/bulk`,
        { contents },
        defaultOptions
    );
}

export function useDestination(destinationId: string) {
    const { data, isValidating, error, mutate } = useSWR(
        destinationId ? `destination_by_id/${destinationId}` : null,
        () => {
            if (destinationId) {
                return getDestination(destinationId);
            }
            return null;
        }
    );
    return {
        destination: data,
        isLoading: isValidating && !data,
        error,
        mutate,
    };
}

export function useDestinationPublicSteps(destinationId: number | null) {
    const { data, isValidating, error } = useSWR(
        destinationId ? ['destination_public_steps', destinationId] : null,
        (_key, id) => getDestinationPublicSteps(id),
    );
    return {
        publicSteps: data ?? [],
        isLoading: isValidating && !data,
        error,
    };
}

export function useCountryDestinationsSearch(query: string) {
    const normalizedQuery = query.trim();
    const { data, isValidating, error } = useSWR(
        normalizedQuery.length >= 3 ? `country_destinations/${normalizedQuery}` : null,
        () =>
            getAllDestinations({
                query: normalizedQuery,
                page: 1,
                resultsPerPage: 100,
                geo_type: DestinationGeoType.COUNTRY,
                sortColumn: 'name',
                sortOrder: 'asc',
            }),
        { revalidateOnFocus: false }
    );

    return {
        countryDestinations: data?.results ?? [],
        isLoading: isValidating && !data,
        isValidating,
        error,
    };
}

export async function createDestinationContentTranslation(
    contentId: number,
    requestBody: DestinationContentTranslationModifySchema
) {
    return post<DestinationContentTranslationSchema>(
        `${BASE_URL}/cms/destinations/contents/${contentId}/translations`,
        requestBody,
        defaultOptions
    );
}

export async function updateDestinationContentTranslation(
    translationId: number,
    requestBody: DestinationContentTranslationModifySchema
) {
    return patch<DestinationContentTranslationSchema>(
        `${BASE_URL}/cms/destinations/translations/${translationId}`,
        requestBody,
        defaultOptions
    );
}

export async function bulkCreateUpdateDestinationContentTranslations(
    contentId: number,
    translations: DestinationContentTranslationModifySchema[]
) {
    return patch<DestinationContentTranslationSchema[]>(
        `${BASE_URL}/cms/destinations/contents/${contentId}/translations/bulk`,
        { translations },
        defaultOptions
    );
}

type DestinationTravelThemeBulkUpdateItem = {
    travel_theme_id: number;
    position: number;
};

export async function updateDestinationTravelThemes(
    destinationId: number,
    travelThemes: DestinationTravelThemeBulkUpdateItem[]
) {
    return put<DestinationTravelThemeSchema[]>(
        `${BASE_URL}/cms/destinations/${destinationId}/travel-themes`,
        { themes: travelThemes },
        defaultOptions
    );
}
